import { useState } from 'react'
import { Link } from 'react-router-dom'
import { AtSign, Plus } from 'lucide-react'
import type { Target } from '@/api/client'
import { useBlueskySettings, useTargets } from '@/lib/queries'
import { TONE_CLASS } from '@/lib/status'
import { cn } from '@/lib/utils'
import {
  Button,
  ButtonLink,
  Dialog,
  EmptyState,
  ErrorState,
  PageHeader,
  PlatformMark,
  Skeleton,
  StatusPill,
  useQueryParam,
} from '@/ui'
import BlueskyPanel, { TestResultLine, useTestBluesky } from './BlueskyPanel'
import { NewAccountDialog } from './NewAccountDialog'
import { CheckLine, PLATFORM_LABEL, tokenInfo, useAccountUsage, useTestAccount, voiceLine } from './shared'

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[4.5rem_1fr] items-start gap-2 text-table">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="min-w-0">{children}</dd>
    </div>
  )
}

function AccountCard({ t, voiceTab }: { t: Target; voiceTab: boolean }) {
  const usage = useAccountUsage()
  const used = usage.byId(t.id)
  const token = tokenInfo(t)
  const test = useTestAccount()
  const persona = voiceLine(t)
  const to = `/accounts/${t.id}${voiceTab ? '?tab=voice' : ''}`
  return (
    <article className="flex flex-col rounded-lg border border-border bg-card p-4">
      <header className="flex items-start gap-2">
        <PlatformMark platform={t.platform} className="mt-1" />
        <div className="min-w-0 flex-1">
          <h2 className="truncate text-section font-semibold">
            <Link to={to} className="hover:underline">
              {t.name}
            </Link>
          </h2>
          <p className="text-meta text-muted-foreground">{PLATFORM_LABEL[t.platform]}</p>
        </div>
        <StatusPill domain="account" status={t.status} />
      </header>

      <dl className="mt-3 flex-1 space-y-1.5">
        <Row label="마지막 점검">
          <CheckLine ok={t.last_check_ok} msg={t.last_check_msg} />
        </Row>
        {token && (
          <Row label="토큰 만료">
            <span
              title={token.detail}
              className={cn('font-medium tabular-nums', token.tone ? TONE_CLASS[token.tone].text : undefined)}
            >
              {token.days === null ? <span className="font-normal text-muted-foreground">{token.label}</span> : token.label}
            </span>
            {token.days !== null && <span className="ml-2 text-meta text-muted-foreground">{token.detail}</span>}
          </Row>
        )}
        <Row label="말투">
          {persona ? (
            <span className="line-clamp-2 text-muted-foreground" title={persona}>
              {persona}
            </span>
          ) : (
            <span className={TONE_CLASS.publishing.text}>말투 없음 — 캠페인 페르소나로 쓴다</span>
          )}
        </Row>
        <Row label="사용처">
          {usage.ready ? (
            <span>
              캠페인 {used.campaigns.length} · 리딤 앱 {used.apps.length}
            </span>
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </Row>
      </dl>

      <footer className="mt-4 flex items-center gap-2">
        <Button size="sm" loading={test.isPending && test.variables === t.id} onClick={() => test.mutate(t.id)}>
          연결 테스트
        </Button>
        <ButtonLink size="sm" variant="ghost" to={to}>
          열기
        </ButtonLink>
      </footer>
    </article>
  )
}

/** The desk's Bluesky login is an account too, but it lives in settings, not
 *  in the targets: the card shows it and tests it, the dialog edits it. */
function BlueskyCard() {
  const q = useBlueskySettings()
  const test = useTestBluesky()
  const [editing, setEditing] = useState(false)
  const s = q.data
  return (
    <article className="flex flex-col rounded-lg border border-border bg-card p-4">
      <header className="flex items-start gap-2">
        <PlatformMark platform="bluesky" className="mt-1" />
        <div className="min-w-0 flex-1">
          <h2 className="truncate text-section font-semibold">{s?.handle || 'Bluesky 데스크'}</h2>
          <p className="text-meta text-muted-foreground">Bluesky · 리딤 데스크</p>
        </div>
      </header>
      {q.error ? (
        <ErrorState className="mt-3" title="불러오지 못했다" error={q.error} onRetry={() => q.refetch()} />
      ) : !s ? (
        <Skeleton className="mt-3 h-20" />
      ) : (
        <dl className="mt-3 flex-1 space-y-1.5">
          <Row label="핸들">{s.handle || <span className="text-muted-foreground">미설정</span>}</Row>
          <Row label="앱 비밀번호">
            <span className={s.password_set ? TONE_CLASS.published.text : TONE_CLASS.failed.text}>
              {s.password_set ? '설정됨' : '미설정'}
            </span>
          </Row>
          <Row label="PDS">
            <span className="break-all text-muted-foreground">{s.pds_url || '—'}</span>
          </Row>
        </dl>
      )}
      <TestResultLine result={test.data} error={test.error} />
      <footer className="mt-4 flex items-center gap-2">
        <Button size="sm" loading={test.isPending} disabled={!s?.password_set} onClick={() => test.mutate()}>
          연결 테스트
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setEditing(true)}>
          설정
        </Button>
      </footer>
      <Dialog open={editing} onClose={() => setEditing(false)} title="Bluesky 데스크 계정" size="lg">
        <div className="max-h-[70vh] overflow-y-auto pb-2">
          <BlueskyPanel />
        </div>
      </Dialog>
    </article>
  )
}

/** /accounts: every account that posts, as a card — connection, token expiry,
 *  voice and where it is used — and the desk's Bluesky login beside them. */
export default function AccountList() {
  const q = useTargets()
  const [tab] = useQueryParam('tab')
  const [creating, setCreating] = useState(false)
  const items = q.data ?? []

  return (
    <>
      <PageHeader
        title="계정"
        actions={
          <Button variant="primary" icon={Plus} onClick={() => setCreating(true)}>
            새 계정
          </Button>
        }
      />
      {q.error ? (
        <ErrorState title="계정을 불러오지 못했다" error={q.error} onRetry={() => q.refetch()} />
      ) : !q.data ? (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          <Skeleton className="h-52" />
          <Skeleton className="h-52" />
        </div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {items.map((t) => (
            <AccountCard key={t.id} t={t} voiceTab={tab === 'voice'} />
          ))}
          <BlueskyCard />
        </div>
      )}
      {q.data && items.length === 0 && (
        <EmptyState
          className="mt-3"
          icon={AtSign}
          title="Threads·Reddit 계정이 아직 없다"
          description="캠페인과 리딤 데스크가 글을 올리려면 계정이 있어야 한다."
          actions={
            <Button variant="primary" icon={Plus} onClick={() => setCreating(true)}>
              새 계정
            </Button>
          }
        />
      )}
      {creating && <NewAccountDialog onClose={() => setCreating(false)} />}
    </>
  )
}
